import React, { useState, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import { GamerFeedbackTabBar } from '../../../../interaxDesignSystemComponents'
import {
  getSectionCardContainerStyles,
  getTabBarContainerStyles
} from '../../../feedbackGenerator/helpers/sectionCardStyles'
import { SectionHeader } from '../../../feedbackGenerator/components/SectionHeader'
import { formatFeedbackText } from '../../../feedbackGenerator/helpers/textFormatter'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import type { MyProfileSectionData } from '../helpers/myProfileHelpers'
import { GlintRevealLayers } from '../../../feedbackGenerator/components/GlintRevealLayers'
import { useGlintRevealCover } from '../../../feedbackGenerator/hooks/useGlintRevealCover'
import DOMPurify from 'dompurify'

export interface SectionTab {
  label: string
  key: string
}

export interface MyProfileSubSectionCardProps {
  /**
   * Translation key for title
   */
  titleKey: string
  /**
   * Translation key for description (optional)
   */
  descriptionKey?: string
  /**
   * Tabs for the section
   */
  tabs: SectionTab[]
  /**
   * Section data containing tab content
   * Simplified format: Record<string, { text: string }>
   */
  sectionData: MyProfileSectionData
  /**
   * Variant for team or individual styling
   */
  variant: 'team' | 'individual'
  /**
   * Optional icon component
   */
  icon?: React.ReactNode
  /** Frosted until the pointer reaches it, while this feedback has not been read */
  isCovered?: boolean
  onRevealed?: () => void
}

/**
 * MyProfileSubSectionCard - Simplified section card for Development Plan sections
 *
 * Features:
 * - Simplified version of ExperimentalSectionCard
 * - No visualization panel (single column text layout)
 * - Frosted cover until hovered while the feedback is unread; no edit functionality
 * - Uses formatFeedbackText for text formatting
 * - Tab navigation with text content display
 */
export const MyProfileSubSectionCard: React.FC<MyProfileSubSectionCardProps> = ({
  titleKey,
  descriptionKey,
  tabs,
  sectionData,
  variant,
  icon,
  isCovered = false,
  onRevealed
}) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.key || '')
  const { containerRef, startReveal, layers } = useGlintRevealCover({ isCovered, onRevealed })

  // Get active tab content
  const activeContent = useMemo(() => {
    const tabData = sectionData[activeTab]
    if (!tabData) return ''

    // Format text using formatFeedbackText helper
    return formatFeedbackText(tabData.text)
  }, [activeTab, sectionData])

  // Get title and description from translation keys
  const title = useMemo(() => t(titleKey), [titleKey, t])
  const description = useMemo(() => (descriptionKey ? t(descriptionKey) : undefined), [descriptionKey, t])

  // Get active tab label
  const activeTabLabel = useMemo(() => {
    const tab = tabs.find((t) => t.key === activeTab)
    return tab?.label || ''
  }, [activeTab, tabs])

  // Sanitize HTML content
  const sanitizedContent = useMemo(() => {
    return DOMPurify.sanitize(activeContent)
  }, [activeContent])

  return (
    <Box
      ref={containerRef}
      onMouseEnter={startReveal}
      onFocus={startReveal}
      sx={{ ...getSectionCardContainerStyles(variant), position: 'relative', overflow: 'hidden' }}
    >
      {/* Section Header */}
      <SectionHeader title={title} description={description} icon={icon} />

      {/* Tab Bar */}
      {tabs.length > 0 && (
        <Box sx={getTabBarContainerStyles()}>
          <GamerFeedbackTabBar
            tabs={tabs.map((tab) => ({
              label: tab.label,
              value: tab.key
            }))}
            activeTab={activeTab}
            onChange={(value) => setActiveTab(value)}
          />
        </Box>
      )}

      {/* Text Content Panel - Single column, full width */}
      {activeContent && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '0px',
            gap: '8px',
            width: '100%',
            alignSelf: 'stretch'
          }}
        >
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.headline6,
              color: INTERAX_COLORS.white,
              alignSelf: 'stretch'
            }}
          >
            {activeTabLabel}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%',
              alignSelf: 'stretch',
              padding: '20px',
              background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '10px',
              backdropFilter: 'blur(6px)',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)'
            }}
          >
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body3,
                color: INTERAX_COLORS.typographyFont,
                margin: 0,
                '& strong': {
                  fontWeight: 600,
                  color: INTERAX_COLORS.typographyFont
                }
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          </Box>
        </Box>
      )}

      <GlintRevealLayers {...layers} />
    </Box>
  )
}

export default MyProfileSubSectionCard
